import React, { useState, useEffect, useRef, useMemo } from "react";
import { checkPowerIssues } from "../utils/compatibility";
import { doublerNeeded } from "../utils/cableMatcher";
import { categorizeSignalCables } from "../utils/signalCableMatcher";
import { lookupConnectorCompat } from "../utils/signalCompatibility";
import { portDisplayLabel } from "../constants/signalLabels";
import CableCombobox from "./CableCombobox";

const CABLE_TYPE_LABELS = {
  standard: "Standard",
  polarity_reverser: "Polarity Reverser",
  current_doubler: "Current Doubler",
  voltage_doubler: "Voltage Doubler",
  voltage_converter: "Voltage Converter",
  splitter: "Splitter",
  microphone: "Microphone",
  instrument: "Instrument",
  speaker: "Speaker",
  balanced_patch: "Balanced Patch",
  midi_cable: "MIDI",
  usb_cable: "USB",
  control_cable: "Control",
  adapter: "Adapter",
  coupler: "Coupler",
  phase_reverser: "Phase Reverser",
  y_cable: "Y-Cable",
};

function findPort(device, handleId) {
  if (!device) return null;
  const ports = device.device_ports || [];
  return ports.find((p) => String(p.id) === handleId);
}

function findPowerOutput(device, handleId) {
  if (!device) return null;
  const match = handleId?.match(/^power-out-(\d+)$/);
  if (!match) return null;
  const outputs = device.power_outputs || [];
  return outputs.find((po) => po.id === Number(match[1]));
}

function noteText(note) {
  return typeof note === "string" ? note : note.message;
}

function noteSeverity(note) {
  return typeof note === "string" ? "error" : note.severity;
}

const SEVERITY_ORDER = { error: 0, warn: 1, info: 2 };

function sortedNotes(notes) {
  return [...notes].sort(
    (a, b) =>
      (SEVERITY_ORDER[noteSeverity(a)] ?? 2) -
      (SEVERITY_ORDER[noteSeverity(b)] ?? 2)
  );
}

function ConnectionNotes({ notes }) {
  if (!notes || notes.length === 0) return null;

  return (
    <ul className="connections-panel-notes">
      {sortedNotes(notes).map((note) => (
        <li
          key={noteText(note)}
          className={`connections-panel-note connections-panel-note--${noteSeverity(note)}`}
        >
          {noteText(note)}
          {note.suggestion && (
            <span className="connections-panel-suggestion"> {note.suggestion}</span>
          )}
        </li>
      ))}
    </ul>
  );
}

function groupLabel(cable) {
  const name = cable.name
    .replace(/\s*\(\d+[""]?\)/, "")
    .replace(/\s*\d+\s*(?:inch|cm|centimeters?)\s*/i, "")
    .replace(/\s+\d{2,4}(?=[A-Z\s]|$)/, "")
    .replace(/\b\d{4}[A-Z]?\b\s*/, "")
    .trim();
  const prefix = cable.manufacturer ? `${cable.manufacturer} ` : "";
  const suffix =
    cable.cable_type !== "standard"
      ? ` (${CABLE_TYPE_LABELS[cable.cable_type] || cable.cable_type})`
      : "";
  return `${prefix}${name}${suffix}`;
}

function groupCables(cables) {
  const groups = new Map();
  for (const cable of cables) {
    const key = [
      cable.manufacturer || "",
      cable.cable_type,
      cable.input_connector || "",
      cable.output_connector || "",
      cable.output_polarity || "",
      cable.input_count || 1,
      cable.output_count || 1,
    ].join("|");
    if (!groups.has(key)) {
      groups.set(key, { label: groupLabel(cable), cable, cables: [] });
    }
    groups.get(key).cables.push(cable);
  }
  return Array.from(groups.values());
}

function cableVoltageOk(cable, sourceVoltageOptions, targetVoltageMin, targetVoltageMax) {
  if (targetVoltageMin == null || targetVoltageMax == null) return true;
  const min = Number(targetVoltageMin);
  const max = Number(targetVoltageMax);

  if (cable.output_voltage) {
    const v = Number(cable.output_voltage);
    return v >= min && v <= max;
  }

  if (!sourceVoltageOptions || sourceVoltageOptions.length === 0) return true;
  return sourceVoltageOptions.some((v) => {
    const volts = Number(v);
    return volts >= min && volts <= max;
  });
}

function categorizeCables(groups, sourceConnector, targetConnector, targetPolarity, sourceVoltageOptions, targetVoltageMin, targetVoltageMax, sourceCurrentMa, targetCurrentMa) {
  const best = [];
  const partial = [];
  const other = [];

  for (const group of groups) {
    const c = group.cable;
    const inputMatch = !sourceConnector || !c.input_connector || c.input_connector === sourceConnector;
    const outputMatch = !targetConnector || !c.output_connector || c.output_connector === targetConnector;
    const polarityMatch = !targetPolarity || !c.output_polarity || c.output_polarity === targetPolarity;
    const voltageOk = cableVoltageOk(c, sourceVoltageOptions, targetVoltageMin, targetVoltageMax);
    const typeOk = doublerNeeded(c, { sourceCurrentMa, targetCurrentMa, sourceVoltageOptions, targetVoltageMin });

    if (inputMatch && outputMatch && polarityMatch && voltageOk && typeOk) {
      best.push(group);
    } else if (outputMatch) {
      partial.push(group);
    } else {
      other.push(group);
    }
  }
  return [
    { label: "Best match", items: best, showByDefault: true },
    { label: "Partial match", items: partial, showByDefault: true },
    { label: "Other", items: other, showByDefault: false },
  ].filter((cat) => cat.items.length > 0);
}

const SENTINEL_NO_CABLE = { value: "__none__", label: "No cable" };
const SENTINEL_SUPPLIED = { value: "__supplied__", label: "Supplied cable" };

function CableSelect({
  cables,
  cablesLoading,
  currentCableId,
  onSelect,
  sourceConnector,
  targetConnector,
  targetPolarity,
  sourceVoltageOptions,
  targetVoltageMin,
  targetVoltageMax,
  sourceCurrentMa,
  targetCurrentMa,
  suppliedAvailable,
  suppliedSelected,
  fromPort,
  toPort,
}) {
  const categories = useMemo(() => {
    const groups = groupCables(cables || []);
    if (fromPort && toPort) {
      return categorizeSignalCables(groups, fromPort, toPort);
    }
    return categorizeCables(groups, sourceConnector, targetConnector, targetPolarity, sourceVoltageOptions, targetVoltageMin, targetVoltageMax, sourceCurrentMa, targetCurrentMa);
  }, [cables, sourceConnector, targetConnector, targetPolarity, sourceVoltageOptions, targetVoltageMin, targetVoltageMax, sourceCurrentMa, targetCurrentMa, fromPort, toPort]);

  const placeholderSentinel = suppliedSelected ? SENTINEL_SUPPLIED : SENTINEL_NO_CABLE;

  const hiddenOptions = useMemo(() => {
    const hidden = new Set();
    for (const cat of categories) {
      if (!cat.showByDefault) {
        for (const g of cat.items) {
          hidden.add(g.cable.id);
        }
      }
    }
    return hidden;
  }, [categories]);

  const allGroups = useMemo(() => {
    const sentinels = suppliedAvailable
      ? [ SENTINEL_SUPPLIED, SENTINEL_NO_CABLE ]
      : [ SENTINEL_NO_CABLE ];
    const mapped = categories.map((cat) => ({
      label: cat.label,
      options: cat.items.map((g) => ({ value: g.cable.id, label: g.label })),
    }));
    return [{ label: "", options: sentinels }, ...mapped];
  }, [categories, suppliedAvailable]);

  let selectedValue = placeholderSentinel;
  if (currentCableId) {
    for (const cat of categories) {
      for (const g of cat.items) {
        if (g.cable.id === currentCableId) {
          selectedValue = { value: g.cable.id, label: g.label };
        }
      }
    }
  }

  const handleChange = (option) => {
    if (!option || option.value === "__none__") {
      onSelect(null, false);
    } else if (option.value === "__supplied__") {
      onSelect(null, true);
    } else {
      const cable = (cables || []).find((c) => c.id === option.value);
      onSelect(cable || null, false);
    }
  };

  const filterOption = (option, inputValue) => {
    if (option.value === "__none__" || option.value === "__supplied__") return true;
    if (!inputValue) return !hiddenOptions.has(option.value);
    return option.label.toLowerCase().includes(inputValue.toLowerCase());
  };

  return (
    <CableCombobox
      groups={allGroups}
      value={selectedValue}
      placeholder={placeholderSentinel.label}
      onChange={handleChange}
      isLoading={cablesLoading}
      filterOption={filterOption}
    />
  );
}

function deviceLabel(device) {
  if (!device) return "Unknown";
  const effects = device.effect_types || [];
  if (effects.length === 0) return device.name;
  return `${device.name} (${effects.map((e) => e.name).join(", ")})`;
}

function cableDisplayName(cable) {
  if (!cable) return null;
  return cable.display_name || `${cable.manufacturer ? cable.manufacturer + " " : ""}${cable.name}`;
}

function ConnectionItem({
  edge,
  nodes,
  styledEdge,
  selected,
  onSelect,
  onDelete,
  onAssignCable,
  onAddChainCable,
  onRemoveChainCable,
  powerCables,
  signalCables,
  cablesLoading,
  cableGroup,
}) {
  const ref = useRef(null);
  const [addingToChain, setAddingToChain] = useState(false);
  const power = edge.data?.connectionType === "power";

  const sourceNode = nodes.find((n) => n.id === edge.source);
  const targetNode = nodes.find((n) => n.id === edge.target);
  const fromDevice = sourceNode?.data?.device;
  const toDevice = targetNode?.data?.device;

  let fromPortName, toPortName, sourceConnector, targetConnector, targetPolarity, sourceVoltageOptions, sourceCurrentMa, targetCurrentMa;
  let fromPort, toPort;
  if (power) {
    const po = fromDevice ? findPowerOutput(fromDevice, edge.sourceHandle) : null;
    fromPortName = po?.name || "Power";
    toPortName = "Power";
    sourceConnector = po?.connector;
    sourceVoltageOptions = po?.voltage_options;
    sourceCurrentMa = po?.current_ma;
    targetConnector = toDevice?.power_connector;
    targetPolarity = toDevice?.power_polarity;
    targetCurrentMa = toDevice?.power_current_ma;
  } else {
    fromPort = fromDevice ? findPort(fromDevice, edge.sourceHandle) : null;
    toPort = toDevice ? findPort(toDevice, edge.targetHandle) : null;
    fromPortName = fromPort ? portDisplayLabel(fromPort) : null;
    toPortName = toPort ? portDisplayLabel(toPort) : null;
  }

  const wireless = !power && (fromPort?.connector === "wireless" || toPort?.connector === "wireless");
  const applicableCables = power ? powerCables : signalCables;

  const notes = styledEdge?.data?.notes || [];
  const cableChain = edge.data?.cableChain || [];

  const attachedCable = edge.data?.attachedCable;
  const directAttach = edge.data?.directAttach;
  const suppliedCable = power && edge.data?.suppliedCable;

  // Eligibility for "Supplied cable" — independent of current state. If the target
  // device declares supplied_power_source_ports that match the source's connector,
  // the picker should always offer "Supplied cable" as an option, even when a regular
  // cable is currently assigned.
  const suppliedAvailable = power && (() => {
    const ports = toDevice?.supplied_power_source_ports || [];
    if (ports.length === 0 || !sourceConnector) return false;
    return ports.some((sp) => lookupConnectorCompat(sourceConnector, sp).level === "ok");
  })();
  const cable = cableChain[0]?.cable;
  const cableName = cable
    ? cableDisplayName(cable)
    : attachedCable ? "Attached cable" : directAttach ? "Direct attached" : suppliedCable ? "Supplied cable" : null;

  // For the "add to chain" picker, shift matching context to the last cable's output
  const lastChainCable = cableChain.length > 0 ? cableChain[cableChain.length - 1].cable : null;
  const chainSourceConnector = lastChainCable ? lastChainCable.output_connector : sourceConnector;

  const cableId = cable?.id;
  useEffect(() => {
    if (selected && ref.current) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [selected, cableId]);

  const fromLabel = sourceNode?.type === "signalBoundary"
    ? sourceNode.data.boundary.label
    : fromDevice ? deviceLabel(fromDevice) : "Unconnected";
  const toLabel = targetNode?.type === "signalBoundary"
    ? targetNode.data.boundary.label
    : toDevice ? deviceLabel(toDevice) : "Unconnected";

  return (
    <li
      ref={ref}
      className={`connections-panel-item${selected ? " connections-panel-item--selected" : ""}${notes.some((n) => noteSeverity(n) === "error") ? " connections-panel-item--has-errors" : notes.some((n) => noteSeverity(n) === "warn") ? " connections-panel-item--has-warns" : notes.some((n) => noteSeverity(n) === "info") ? " connections-panel-item--has-infos" : ""}`}
    >
      <button
        type="button"
        className="connections-panel-item-header"
        onClick={() => onSelect(edge.id)}
      >
        <div className="connections-panel-devices">
          <span className="connections-panel-device">
            {fromLabel}
          </span>
          {fromPortName && (
            <span className="connections-panel-port">{fromPortName}</span>
          )}
          <span className="connections-panel-arrow">&rarr;</span>
          <span className="connections-panel-device">
            {toLabel}
          </span>
          {toPortName && (
            <span className="connections-panel-port">{toPortName}</span>
          )}
        </div>
        {cableName && cableChain.length <= 1 && (
          <span className="connections-panel-cable-badge">
            {cableName}
            {cableGroup && (
              <span className="connections-panel-cable-group">
                {` ${cableGroup.index} of ${cableGroup.total}`}
              </span>
            )}
          </span>
        )}
        {cableChain.length > 1 && (
          <span className="connections-panel-cable-badge">
            {cableChain.map((entry, i) => {
              const name = cableDisplayName(entry.cable);
              const isGrouped = cableGroup && i === 0;
              return (
                <span key={i}>
                  {i > 0 && " → "}
                  {name}
                  {isGrouped && (
                    <span className="connections-panel-cable-group">
                      {` ${cableGroup.index} of ${cableGroup.total}`}
                    </span>
                  )}
                </span>
              );
            })}
          </span>
        )}
      </button>
      <ConnectionNotes notes={notes} />
      {selected && (
        <div className="connections-panel-actions">
          {!attachedCable && !directAttach && !wireless && cableChain.length === 0 && (
            <CableSelect
              cables={applicableCables || []}
              cablesLoading={cablesLoading}
              currentCableId={cableId}
              onSelect={(cbl, isSupplied) => onAssignCable(edge.id, cbl, isSupplied)}
              sourceConnector={power ? sourceConnector : fromPort?.connector}
              targetConnector={power ? targetConnector : toPort?.connector}
              targetPolarity={power ? targetPolarity : null}
              sourceVoltageOptions={power ? sourceVoltageOptions : null}
              targetVoltageMin={power ? toDevice?.power_voltage_min : null}
              targetVoltageMax={power ? toDevice?.power_voltage_max : null}
              sourceCurrentMa={power ? sourceCurrentMa : null}
              targetCurrentMa={power ? targetCurrentMa : null}
              suppliedAvailable={suppliedAvailable}
              suppliedSelected={suppliedCable}
              fromPort={!power ? fromPort : null}
              toPort={!power ? toPort : null}
            />
          )}
          {!attachedCable && !directAttach && !wireless && cableChain.length > 0 && (
            <div className="connections-panel-chain">
              {cableChain.map((entry, i) => (
                <div key={entry.rigCableId || entry.rigCableTempId || i} className="connections-panel-chain-item">
                  <span className="connections-panel-chain-label">
                    {i + 1}. {cableDisplayName(entry.cable)}
                  </span>
                  <button
                    type="button"
                    className="connections-panel-chain-remove"
                    onClick={() => onRemoveChainCable(edge.id, i)}
                    aria-label={`Remove ${cableDisplayName(entry.cable)}`}
                  >
                    &times;
                  </button>
                </div>
              ))}
              {addingToChain ? (
                <CableSelect
                  cables={applicableCables || []}
                  cablesLoading={cablesLoading}
                  currentCableId={null}
                  onSelect={(cbl) => {
                    if (cbl) onAddChainCable(edge.id, cbl);
                    setAddingToChain(false);
                  }}
                  sourceConnector={power ? chainSourceConnector : fromPort?.connector}
                  targetConnector={power ? targetConnector : toPort?.connector}
                  targetPolarity={power ? targetPolarity : null}
                  sourceVoltageOptions={power ? sourceVoltageOptions : null}
                  targetVoltageMin={power ? toDevice?.power_voltage_min : null}
                  targetVoltageMax={power ? toDevice?.power_voltage_max : null}
                  fromPort={!power ? fromPort : null}
                  toPort={!power ? toPort : null}
                />
              ) : (
                <button
                  type="button"
                  className="connections-panel-chain-add"
                  onClick={() => setAddingToChain(true)}
                >
                  + Add cable or adapter
                </button>
              )}
            </div>
          )}
          <button
            type="button"
            className="connections-panel-delete"
            onClick={() => onDelete(edge.id)}
          >
            Delete connection
          </button>
        </div>
      )}
    </li>
  );
}

function OrphanPowerItem({ issue }) {
  return (
    <li className="connections-panel-item connections-panel-item--orphan">
      <div className="connections-panel-item-header">
        <div className="connections-panel-devices">
          <span className="connections-panel-device">{issue.device}</span>
        </div>
      </div>
      <ConnectionNotes notes={issue.notes} />
    </li>
  );
}

export default function ConnectionsPanel({
  nodes,
  edges,
  styledEdges,
  selectedEdgeId,
  onSelectEdge,
  onAssignCable,
  onAddChainCable,
  onRemoveChainCable,
  onDeleteConnection,
  powerCables,
  signalCables,
  cablesLoading,
}) {
  const powerIssues = checkPowerIssues(nodes, edges);
  const orphanIssues = powerIssues.filter((i) => !i.edgeId);

  const signalEdges = edges.filter((e) => e.data?.connectionType === "signal" || !e.data?.connectionType);
  const powerEdges = edges.filter((e) => e.data?.connectionType === "power");

  const styledEdgeMap = useMemo(() => {
    const map = new Map();
    for (const se of (styledEdges || [])) map.set(se.id, se);
    return map;
  }, [styledEdges]);

  // Build cable group info: edgeId → { index, total }
  const cableGroupMap = useMemo(() => {
    const map = new Map();
    const byRef = new Map();
    for (const e of edges) {
      const first = e.data?.cableChain?.[0];
      const ref = first?.rigCableId || first?.rigCableTempId;
      if (!ref) continue;
      if (!byRef.has(ref)) byRef.set(ref, []);
      byRef.get(ref).push(e.id);
    }
    for (const edgeIds of byRef.values()) {
      if (edgeIds.length < 2) continue;
      edgeIds.forEach((id, i) => {
        map.set(id, { index: i + 1, total: edgeIds.length });
      });
    }
    return map;
  }, [edges]);

  if (signalEdges.length === 0 && powerEdges.length === 0 && orphanIssues.length === 0) {
    return (
      <div className="connections-panel-empty">No connections yet.</div>
    );
  }

  const edgeSeverity = (e) => {
    const styled = styledEdgeMap.get(e.id);
    if (styled?.type === "warning") return 0;
    if (styled?.type === "warn") return 1;
    return 3;
  };

  const KIND_RANK = { signal: 0, power: 1 };
  const entries = [];
  for (const e of signalEdges) {
    entries.push({ kind: "signal", severity: edgeSeverity(e), edge: e });
  }
  for (const e of powerEdges) {
    entries.push({ kind: "power", severity: edgeSeverity(e), edge: e });
  }
  entries.sort(
    (a, b) => (a.severity - b.severity) || (KIND_RANK[a.kind] - KIND_RANK[b.kind])
  );

  const elements = [];
  let lastKind = null;

  for (const entry of entries) {
    if (entry.kind !== lastKind) {
      elements.push(
        <li key={`header-${entry.kind}-${entry.severity}`} className="connections-panel-section-title" role="presentation">
          {entry.kind === "signal" ? "Signal" : "Power"}
        </li>
      );
      lastKind = entry.kind;
    }

    elements.push(
      <ConnectionItem
        key={entry.edge.id}
        edge={entry.edge}
        nodes={nodes}
        styledEdge={styledEdgeMap.get(entry.edge.id)}
        selected={entry.edge.id === selectedEdgeId}
        onSelect={onSelectEdge}
        onDelete={onDeleteConnection}
        onAssignCable={onAssignCable}
        onAddChainCable={onAddChainCable}
        onRemoveChainCable={onRemoveChainCable}
        powerCables={powerCables}
        signalCables={signalCables}
        cablesLoading={cablesLoading}
        cableGroup={cableGroupMap.get(entry.edge.id)}
      />
    );
  }

  if (orphanIssues.length > 0) {
    if (lastKind !== "power") {
      elements.push(
        <li key="header-power-orphan" className="connections-panel-section-title" role="presentation">Power</li>
      );
    }
    for (const issue of orphanIssues) {
      elements.push(<OrphanPowerItem key={issue.nodeId} issue={issue} />);
    }
  }

  const totalDrawMa = nodes.reduce((sum, node) => {
    const device = node.data?.device;
    if (!device) return sum;
    return sum + (device.power_current_ma || 0);
  }, 0);

  const hasPower = powerEdges.length > 0 || orphanIssues.length > 0;

  return (
    <div className="connections-panel">
      <ul className="connections-panel-list">
        {elements}
      </ul>
      {hasPower && totalDrawMa > 0 && (
        <div className="connections-panel-total-draw">
          Total draw: {totalDrawMa}mA
        </div>
      )}
    </div>
  );
}
